import type { TEvent } from "@apac/checkout";
import { Event } from "@checkout/events";
import {
  CardPaymentActionTypes,
  CardPaymentEventTypes,
  FlightExchangeActionTypes,
  getCardPaymentMachine,
  getCartFulfillMachine,
  getCartQuoteMachine,
  getReviewMachineState,
} from "@checkout/states";
import { trackPollFailure, trackPollSuccess } from "@checkout/tracking";
import {
  FlightExchangeState,
  GenericChildState,
  ParentState,
} from "@checkout/types";
import { COMPLETE_BUY_AIR } from "@hopper-b2b/types";
import { RewardsPaymentActionTypes } from "@hsbc/rewards";
import type { MachineConfig } from "xstate";
import { getInitialFlightExchangeMachineContext } from "./initialContext";
import type { FlightExchangeMachineContext } from "./types";

const flightExchangeMachineId = "flight-exchange-checkout";

const cartQuoteNestedStateMachine = getCartQuoteMachine({
  failedState: `#${flightExchangeMachineId}.${FlightExchangeState.cartRebookFailed}`,
  parentMachineId: flightExchangeMachineId,
  nextState: `#${flightExchangeMachineId}.${ParentState.cardPayment}`,
});
const cardPaymentNestedStateMachine = getCardPaymentMachine({
  nextState: `#${flightExchangeMachineId}.${ParentState.review}`,
  parentMachineId: flightExchangeMachineId,
  previousState: `#${flightExchangeMachineId}.${ParentState.cartQuote}`,
});
const reviewNestedStateMachine = getReviewMachineState({
  nextState: `#${flightExchangeMachineId}.${ParentState.cartFulfill}`,
  parentMachineId: flightExchangeMachineId,
  previousState: `#${flightExchangeMachineId}.${ParentState.cardPayment}`,
});
const cartFulfillNestedStateMachine = getCartFulfillMachine({
  failedState: `#${flightExchangeMachineId}.${FlightExchangeState.cartRebookFailed}`,
  parentMachineId: flightExchangeMachineId,
  nextState: `#${flightExchangeMachineId}.${ParentState.bookingConfirmation}`,
  trackPollFailureAction: trackPollFailure({
    eventName: COMPLETE_BUY_AIR,
  }),
  trackPollSuccessAction: trackPollSuccess({
    eventName: COMPLETE_BUY_AIR,
  }),
});

export const flightExchangeCheckoutMachine: MachineConfig<
  FlightExchangeMachineContext,
  any,
  TEvent
> = {
  id: flightExchangeMachineId,
  initial: ParentState.cartQuote,
  on: {
    [CardPaymentEventTypes.SET_SELECTED_PAYMENT_METHOD_ID]: {
      actions: CardPaymentActionTypes.setSelectedPaymentMethodId,
    },
    [Event.GO_TO_REVIEW]: `#${flightExchangeMachineId}.${ParentState.review}`,
    [Event.GO_TO_CART_QUOTE]: `#${flightExchangeMachineId}.${ParentState.cartQuote}`,
    [Event.GO_TO_CARD_PAYMENT]: `#${flightExchangeMachineId}.${ParentState.cardPayment}`,
    [Event.GO_TO_CART_FULFILL]: `#${flightExchangeMachineId}.${ParentState.cartFulfill}`,
    [Event.GO_TO_BOOKING_CONFIRMATION]: `#${flightExchangeMachineId}.${ParentState.bookingConfirmation}`,
    ...cartQuoteNestedStateMachine.on,
    ...cardPaymentNestedStateMachine.on,
    ...reviewNestedStateMachine.on,
    ...cartFulfillNestedStateMachine.on,
  },
  schema: {
    context: getInitialFlightExchangeMachineContext(),
    events: {} as TEvent,
  },
  states: {
    [ParentState.cartQuote]: {
      ...cartQuoteNestedStateMachine,
      entry: FlightExchangeActionTypes.addExchangeFlightProductMod,
    },
    [ParentState.cardPayment]: cardPaymentNestedStateMachine,
    [ParentState.review]: {
      ...reviewNestedStateMachine,
      initial: GenericChildState.loading,
    },
    [ParentState.cartFulfill]: {
      ...cartFulfillNestedStateMachine,
      entry: RewardsPaymentActionTypes.setPaymentFulfillParams,
    },
    [FlightExchangeState.cartRebookFailed]: {},
    [ParentState.bookingConfirmation]: {},
  },
};
